import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { SITE_URL, canonicalUrl } from '../seo/siteRoutes.js';

const PAGE_PATH = '/service-quality';
const PAGE_URL = canonicalUrl(PAGE_PATH);
const META_DESCRIPTION =
  'כך מתנהל השירות ב-Printeam: בחירת מוצר, העלאת עיצוב, בדיקת הכמויות, תשלום ומעקב אחר ההזמנה.';

export default function ServiceQuality() {
  return (
    <div className="min-h-screen py-12">
      <Helmet>
        {/* Structured data: Organization, Article, BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Printeam",
            url: canonicalUrl('/'),
            logo: `${SITE_URL}/logo_printee-optimized.webp`
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "תהליך השירות וההזמנה – Printeam",
            description: META_DESCRIPTION,
            author: { "@type": "Organization", name: "Printeam" },
            publisher: {
              "@type": "Organization",
              name: "Printeam",
              logo: { "@type": "ImageObject", url: `${SITE_URL}/logo_printee-optimized.webp` }
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": PAGE_URL }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "ראשי", item: canonicalUrl('/') },
              { "@type": "ListItem", position: 2, name: "איכות השירות", item: PAGE_URL }
            ]
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-6">איכות השירות</h1>

        <section>
          <h2 className="text-2xl font-semibold mt-4 mb-2">תהליך ברור מהבחירה ועד ההזמנה</h2>
          <p className="text-gray-700">
            ההזמנה באתר בנויה בשלבים: בוחרים מוצר, צבעים ומידות, מגדירים אזורי
            מיתוג, מעלים את העיצוב ובודקים את סיכום המחיר לפני המעבר לתשלום.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">לפני הוספה לסל</h2>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>בחרו לפחות צבע, מידה וכמות עבור המוצר.</li>
            <li>הגדירו אזור מיתוג פעיל והעלו קובץ מתאים.</li>
            <li>עברו על הודעות הבדיקה שמוצגות במעצב.</li>
            <li>ודאו שהמחיר משקף את הכמות ואת אפשרויות המיתוג שנבחרו.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">הכנת קובץ לעיצוב</h2>
          <p className="text-gray-700">
            איכות הקובץ משפיעה על האפשרות להפיק את העיצוב בגודל שנבחר. מומלץ
            להעלות קובץ חד, ללא רקע כאשר הדבר מתאים, ולמקם אותו בתוך אזור המיתוג.
          </p>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>הפורמטים המומלצים מפורטים בעמוד השאלות הנפוצות.</li>
            <li>התצוגה באתר מסייעת למיקום; יש לבדוק את הסיכום לפני אישור ההזמנה.</li>
            <li>אם נדרשת התאמה שאינה זמינה במעצב, אפשר לפנות לפני התשלום.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">אישור, תשלום ומעקב</h2>
          <p className="text-gray-700">
            לאחר בחירת הפריטים והמיתוג, סל הקניות מרכז את פרטי ההזמנה. זמני
            הייצור והמשלוח המוצגים באתר הם הערכה ותלויים בכמות, באישור העיצוב
            ובפרטי ההזמנה.
          </p>
          <ul className="list-disc ml-6 mt-3 text-gray-700">
            <li>בדקו את כל השורות בסל לפני התשלום.</li>
            <li>שמרו את אישור ההזמנה ופרטי העסקה.</li>
            <li>לפנייה בנוגע להזמנה קיימת, צרפו את מזהה ההזמנה.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mt-6 mb-2">צריכים מידע נוסף?</h2>
          <p className="text-gray-700">
            עברו אל <Link to="/faq" className="text-blue-600 hover:underline">השאלות הנפוצות</Link> למידע
            על קבצים, כמויות ומשלוחים, או אל{' '}
            <Link to="/contact" className="text-blue-600 hover:underline">עמוד יצירת הקשר</Link> לפני
            השלמת הזמנה שדורשת בירור.
          </p>
        </section>
      </div>
    </div>
  );
}
